import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import Table from '@app/src/components/Table'
import { getInquiryExportColumnsTransparency } from '@app/src/export-columns/inquiry'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import { useHasInquiriesWithProduct } from '@app/src/hooks/useHasInquiriesWithProduct'
import ProviderInquiryHeader from '@app/src/pages/ResourceCollection/Collections/Request/ProviderInquiryHeader'
import ProviderInquiryRow from '@app/src/pages/ResourceCollection/Collections/Request/ProviderInquiryRow'
import ProviderRequestsFilters from '@app/src/pages/ResourceCollection/Filters/ProviderRequestsFilters'
import type { ResourceCollectionSceneProps } from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { Inquiry } from '@app/src/types/resourceExplorer'
import { ResourceTypes } from '@app/src/wf-constants'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { ViewTypeName } from './ManageRequestsScene'

const allowedFilters = [
  'template.id',
  'status',
  'creatorOrganization.id',
  'request.responseExternalStatus',
  'request.response.id',
  'deadline',
  'activatedAt',
  'creatorUserId',
]

type ProviderRequestsSceneProps = {
  tabs?: ResourceCollectionSceneProps['tabs']
  actionButtons?: ResourceCollectionSceneProps['actionButtons']
}

const ProviderRequestsScene: React.FC<ProviderRequestsSceneProps> = ({ tabs, actionButtons = [] }) => {
  const { formatMessage } = useIntl()
  const { sorting, toggleSorting } = useSort()
  const apiFilters = useGetApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery(undefined, [apiFilters])
  const { openDrawer } = useDrawer()

  const basePayload = {
    filter: [],
    sort: sorting,
    include: [
      'creatorOrganization',
      'creatorOrganization.country',
      'creatorOrganization.industryNaceCode',
      'creatorOrganization.image',
      'creatorOrganization.locations',
      'template',
      'template.image',
      'request.responses.verifications',
      'targetProduct',
    ],
  }

  const payload = {
    ...basePayload,
    filter: [...basePayload.filter, ...apiFilters],
    pagination: {
      pageNumber: page,
      itemsPerPage: pageSize,
    },
  }

  const { items, count, isLoading, isFetching, isError } = useFetchCollectionWithPost<Inquiry>({
    key: FetchKey.Request,
    endpoint: endpoints.inquiriesCollection,
    payload,
  })

  const hasInquiriesWithProduct = useHasInquiriesWithProduct()

  const showTargetProductColumn = useMemo(() => items.some(inquiry => inquiry.targetProduct), [items])

  return (
    <>
      <ResourceCollectionScene
        title={formatMessage({ id: 'navbar.manageRequests' })}
        tabs={tabs}
        actionButtons={[
          {
            label: formatMessage({ id: 'resourceCollections.general.export' }),
            variant: 'outlined',
            onClick: () =>
              openDrawer(
                <DrawerViewExport
                  resourceType={ResourceTypes.Inquiry}
                  resourceView={tabs?.activeTabParam}
                  count={count}
                  userFilter={apiFilters}
                  exportColumns={getInquiryExportColumnsTransparency(hasInquiriesWithProduct)}
                  rawExportPayload={basePayload}
                />,
              ),
            disabled: tabs?.activeTabParam === ViewTypeName.Overview || isLoading || count === 0,
          },
          ...actionButtons,
        ]}
        filter={<ProviderRequestsFilters allowedFilters={allowedFilters} resetPage={resetPage} />}
      >
        <Table<Inquiry>
          RowComponent={ProviderInquiryRow}
          rowComponentProps={{ showTargetProductColumn: showTargetProductColumn }}
          HeaderComponent={() => (
            <ProviderInquiryHeader
              toggleSorting={toggleSorting}
              activeSorting={sorting}
              showTargetProductColumn={showTargetProductColumn}
            />
          )}
          data={items}
          isLoading={isLoading || isFetching}
          count={count}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </ResourceCollectionScene>
    </>
  )
}

export default ProviderRequestsScene
